/**
 * PKCE helpers for OAuth 2.0 Authorization Code + PKCE (RFC 7636).
 *
 * - generateCodeVerifier: 64-char base64url-safe random string
 * - deriveCodeChallenge: SHA-256(verifier) → base64url, S256 method
 * - generateState: 32-char random opaque string for CSRF protection
 *
 * Uses Web Crypto exclusively. No polyfills required for evergreen
 * browsers; admin-v5 declares tsc target es2022.
 */

const VERIFIER_BYTES = 48; // → 64 base64url chars
const STATE_BYTES = 24; // → 32 base64url chars

function base64urlEncode(bytes: Uint8Array): string {
  let s = '';
  for (const byte of bytes) s += String.fromCharCode(byte);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateCodeVerifier(): string {
  const buf = new Uint8Array(VERIFIER_BYTES);
  crypto.getRandomValues(buf);
  return base64urlEncode(buf);
}

export function generateState(): string {
  const buf = new Uint8Array(STATE_BYTES);
  crypto.getRandomValues(buf);
  return base64urlEncode(buf);
}

export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64urlEncode(new Uint8Array(digest));
}
